/* ==========================================================================
   NUVI KIDZ WORLD — shared behaviour for the Parenting & Kids World pages.
   Vanilla JS, no dependencies. Loaded (deferred) only on World pages.

   Privacy: tools keep what parents type in this browser only (localStorage,
   keys prefixed "nkw:"). Nothing is sent to a server, and no tool asks for a
   child's name, photo, birth date or health record.
   ========================================================================== */
(function () {
  'use strict';

  var PREFIX = 'nkw:';
  var DAY_MS = 864e5;

  /* ---------- storage (never throws) ---------- */
  var store = {
    get: function (key, fallback) {
      try {
        var raw = window.localStorage.getItem(PREFIX + key);
        return raw == null ? fallback : JSON.parse(raw);
      } catch (e) { return fallback; }
    },
    set: function (key, value) {
      try { window.localStorage.setItem(PREFIX + key, JSON.stringify(value)); return true; } catch (e) { return false; }
    },
    remove: function (key) {
      try { window.localStorage.removeItem(PREFIX + key); } catch (e) { /* ignore */ }
    }
  };

  function $all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 7); }

  /* ---------- pure helpers (exported for tests) ---------- */

  /** Parses YYYY-MM-DD as a local date (no timezone drift). */
  function parseDate(value) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
    if (!m) return null;
    var d = new Date(+m[1], +m[2] - 1, +m[3]);
    return d.getMonth() === +m[2] - 1 ? d : null;
  }
  function startOfDay(d) { return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }

  /**
   * Naegele-style estimate: 280 days from the first day of the last period,
   * shifted by (cycle length - 28). Returns { error } or the estimate.
   * It is an estimate only; clinicians often date a pregnancy by ultrasound.
   */
  function estimateDueDate(lmpValue, cycleLength, today) {
    var lmp = parseDate(lmpValue);
    if (!lmp) return { error: 'missing' };
    var now = startOfDay(today || new Date());
    var cycle = parseInt(cycleLength, 10);
    if (!(cycle >= 21 && cycle <= 45)) cycle = 28;
    if (lmp > now) return { error: 'future' };
    var daysAlong = Math.round((now - lmp) / DAY_MS);
    if (daysAlong > 44 * 7) return { error: 'too-old' };
    var due = new Date(lmp.getFullYear(), lmp.getMonth(), lmp.getDate() + 280 + (cycle - 28));
    var adjusted = Math.max(0, daysAlong - (cycle - 28));
    var weeks = Math.floor(adjusted / 7);
    return {
      due: due,
      weeks: weeks,
      days: adjusted % 7,
      trimester: weeks < 13 ? 1 : weeks < 28 ? 2 : 3,
      daysToGo: Math.round((due - now) / DAY_MS)
    };
  }

  /** Percentage of checked items, 0–100, rounded. */
  function percent(done, total) { return total > 0 ? Math.round((done / total) * 100) : 0; }

  /** Splits "a, b; c" / "a b" style lists from data attributes. */
  function tokens(value) { return String(value || '').toLowerCase().split(/[\s,;|]+/).filter(Boolean); }

  /**
   * Decides whether an item passes the active filters.
   * filters: { key: value } where value '' or 'all' means no filter;
   * item: { key: 'space separated values' }; query matches item.text.
   */
  function matches(item, filters, query) {
    for (var k in filters) {
      if (!Object.prototype.hasOwnProperty.call(filters, k)) continue;
      var want = String(filters[k] || '').toLowerCase();
      if (!want || want === 'all') continue;
      var have = tokens(item[k]);
      if (have.indexOf(want) === -1 && have.indexOf('any') === -1) return false;
    }
    if (query) {
      var words = String(query).toLowerCase().trim().split(/\s+/);
      var text = String(item.text || '').toLowerCase();
      for (var i = 0; i < words.length; i++) if (words[i] && text.indexOf(words[i]) === -1) return false;
    }
    return true;
  }

  /** Small deterministic PRNG so a plan only changes when the answers change. */
  function seeded(seedText) {
    var h = 2166136261;
    for (var i = 0; i < seedText.length; i++) { h ^= seedText.charCodeAt(i); h = Math.imul(h, 16777619); }
    return function () {
      h += 0x6D2B79F5; var t = h;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  /**
   * Builds a 7-day plan from idea records:
   *   { title, url, ages:'1-2 2-3', place:'indoor|outdoor|any', materials:'none|home|art', theme, minutes }
   * Each day prefers the day's theme, then any matching idea; no idea repeats
   * while unused matches remain. Returns [{ day, theme, idea|null }].
   */
  var WEEK_THEMES = [
    ['Mon', 'colours'], ['Tue', 'stories'], ['Wed', 'shapes'], ['Thu', 'music'],
    ['Fri', 'nature'], ['Sat', 'creativity'], ['Sun', 'family']
  ];
  function buildWeek(ideas, prefs, seedText) {
    prefs = prefs || {};
    var rand = seeded(seedText || JSON.stringify(prefs));
    var fit = ideas.filter(function (idea) {
      if (prefs.age && tokens(idea.ages).indexOf(prefs.age) === -1) return false;
      if (prefs.place && prefs.place !== 'any' && idea.place !== 'any' && idea.place !== prefs.place) return false;
      if (prefs.materials === 'none' && idea.materials !== 'none') return false;
      if (prefs.materials === 'home' && idea.materials === 'art') return false;
      if (prefs.minutes && idea.minutes && +idea.minutes > +prefs.minutes + 10) return false;
      return true;
    });
    var used = {};
    function pick(list) {
      var free = list.filter(function (i) { return !used[i.title]; });
      var pool = free.length ? free : list;
      if (!pool.length) return null;
      var chosen = pool[Math.floor(rand() * pool.length)];
      used[chosen.title] = true;
      return chosen;
    }
    return WEEK_THEMES.map(function (pair, i) {
      var theme = pair[1];
      var interest = prefs.interest && prefs.interest !== 'all' && (i % 2 === 0) ? prefs.interest : null;
      var idea = (interest && pick(fit.filter(function (x) { return x.theme === interest; }))) ||
        pick(fit.filter(function (x) { return x.theme === theme; })) ||
        pick(fit);
      return { day: pair[0], theme: idea ? idea.theme : theme, idea: idea };
    });
  }

  /* ---------- print just one element ---------- */
  function printElement(el) {
    if (!el) return;
    el.classList.add('nkw-print-target');
    document.body.classList.add('nkw-printing');
    var done = function () {
      el.classList.remove('nkw-print-target');
      document.body.classList.remove('nkw-printing');
      window.removeEventListener('afterprint', done);
    };
    window.addEventListener('afterprint', done);
    try { window.print(); } catch (e) { /* ignore */ }
    setTimeout(done, 1500);
  }

  /* ---------- inline confirm (no browser dialogs) ---------- */
  function confirmInline(anchor, message, onYes) {
    var box = anchor.parentNode.querySelector('.nkw-confirm');
    if (!box) {
      box = document.createElement('div');
      box.className = 'nkw-confirm';
      box.setAttribute('role', 'group');
      anchor.parentNode.appendChild(box);
    }
    box.innerHTML = '<span>' + esc(message) + '</span>' +
      '<button type="button" class="nkw-btn nkw-btn--green nkw-btn--sm" data-yes>Yes, clear</button>' +
      '<button type="button" class="nkw-btn nkw-btn--ghost nkw-btn--sm" data-no>Keep</button>';
    box.hidden = false;
    box.querySelector('[data-no]').focus();
    box.onclick = function (e) {
      if (e.target.closest('[data-yes]')) { box.hidden = true; onYes(); anchor.focus(); }
      else if (e.target.closest('[data-no]')) { box.hidden = true; anchor.focus(); }
    };
  }

  /* ---------- tabs ---------- */
  function initTabs(root) {
    $all('[data-nkw-tabs]', root).forEach(function (list) {
      if (list.__nkw) return; list.__nkw = true;
      var tabs = $all('[role="tab"]', list);
      function select(tab, focus) {
        tabs.forEach(function (t) {
          var on = t === tab;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
          var panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel && panel.getAttribute('data-nkw-shared') == null) panel.hidden = !on;
        });
        if (focus) tab.focus();
        list.dispatchEvent(new CustomEvent('nkw:tab', { detail: { tab: tab }, bubbles: true }));
      }
      tabs.forEach(function (t, i) {
        t.addEventListener('click', function () { select(t); });
        t.addEventListener('keydown', function (e) {
          var n = null;
          if (e.key === 'ArrowRight') n = tabs[(i + 1) % tabs.length];
          else if (e.key === 'ArrowLeft') n = tabs[(i - 1 + tabs.length) % tabs.length];
          else if (e.key === 'Home') n = tabs[0];
          else if (e.key === 'End') n = tabs[tabs.length - 1];
          if (n) { e.preventDefault(); select(n, true); }
        });
      });
      var start = tabs.filter(function (t) { return t.getAttribute('aria-selected') === 'true'; })[0] || tabs[0];
      if (start) select(start);
    });
  }

  /* ---------- filters (chips, selects, search) over [data-nkw-item] ---------- */
  function initFilters(root) {
    $all('[data-nkw-filter]', root).forEach(function (box) {
      if (box.__nkw) return; box.__nkw = true;
      var items = $all('[data-nkw-item]', box);
      var count = box.querySelector('[data-nkw-count]');
      var empty = box.querySelector('[data-nkw-empty]');
      var search = box.querySelector('[data-nkw-search]');
      var state = {};
      var params = {};
      try { new URLSearchParams(location.search).forEach(function (v, k) { params[k] = v; }); } catch (e) { /* old browser */ }
      $all('[data-filter-key]', box).forEach(function (ctl) {
        var key = ctl.getAttribute('data-filter-key');
        var wanted = params[key];
        if (wanted && ctl.tagName === 'SELECT' && ctl.querySelector('option[value="' + wanted.replace(/"/g, '') + '"]')) ctl.value = wanted;
        if (wanted && ctl.tagName === 'BUTTON') ctl.setAttribute('aria-pressed', ctl.getAttribute('data-filter-value') === wanted ? 'true' : 'false');
        if (ctl.tagName === 'SELECT') {
          state[key] = ctl.value;
          ctl.addEventListener('change', function () { state[key] = ctl.value; apply(); });
        } else if (ctl.getAttribute('aria-pressed') === 'true') {
          state[key] = ctl.getAttribute('data-filter-value');
        }
      });
      box.addEventListener('click', function (e) {
        var chip = e.target.closest('button[data-filter-key][data-filter-value]');
        if (!chip || !box.contains(chip)) return;
        var key = chip.getAttribute('data-filter-key');
        $all('button[data-filter-key="' + key + '"]', box).forEach(function (c) { c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'); });
        state[key] = chip.getAttribute('data-filter-value');
        apply();
      });
      if (search) {
        search.addEventListener('input', apply);
        var form = search.form;
        if (form && form.hasAttribute('data-nkw-local')) form.addEventListener('submit', function (e) { e.preventDefault(); apply(); });
      }
      function apply() {
        var q = search ? search.value : '';
        var shown = 0;
        items.forEach(function (el) {
          var data = { text: el.getAttribute('data-text') || el.textContent };
          for (var k in state) data[k] = el.getAttribute('data-' + k);
          var ok = matches(data, state, q);
          el.hidden = !ok;
          if (ok) shown++;
        });
        if (count) count.textContent = shown === 1 ? '1 result' : shown + ' results';
        if (empty) empty.hidden = shown > 0;
      }
      apply();
    });
  }

  /* ---------- dialogs ---------- */
  function initDialogs(root) {
    $all('[data-nkw-dialog-open]', root).forEach(function (btn) {
      if (btn.__nkw) return; btn.__nkw = true;
      btn.addEventListener('click', function () {
        var dlg = document.getElementById(btn.getAttribute('data-nkw-dialog-open'));
        if (!dlg) return;
        dlg.__opener = btn;
        if (typeof dlg.showModal === 'function') dlg.showModal(); else dlg.setAttribute('open', '');
        var close = dlg.querySelector('[data-nkw-dialog-close]');
        if (close) close.focus();
      });
    });
    $all('dialog.nkw-dialog', root).forEach(function (dlg) {
      if (dlg.__nkw) return; dlg.__nkw = true;
      function shut() {
        if (typeof dlg.close === 'function') dlg.close(); else dlg.removeAttribute('open');
      }
      dlg.addEventListener('click', function (e) {
        if (e.target === dlg || e.target.closest('[data-nkw-dialog-close]')) shut();
      });
      dlg.addEventListener('close', function () { if (dlg.__opener) dlg.__opener.focus(); });
    });
  }

  /* ---------- print buttons ---------- */
  function initPrint(root) {
    $all('[data-nkw-print]', root).forEach(function (btn) {
      if (btn.__nkw) return; btn.__nkw = true;
      btn.addEventListener('click', function () {
        var sel = btn.getAttribute('data-nkw-print');
        printElement(sel ? document.querySelector(sel) : btn.closest('.nkw-panel, .nkw-section'));
      });
    });
  }

  /* ---------- checklists ---------- */
  function initChecklists(root) {
    $all('[data-nkw-checklist]', root).forEach(function (box) {
      if (box.__nkw) return; box.__nkw = true;
      var key = 'check:' + box.getAttribute('data-nkw-checklist');
      var saved = store.get(key, { done: {}, custom: [] });
      if (!saved || typeof saved !== 'object') saved = { done: {}, custom: [] };
      saved.done = saved.done || {}; saved.custom = saved.custom || [];
      var ring = box.querySelector('.nkw-ring');
      var pctEl = box.querySelector('[data-nkw-pct]');
      var countEl = box.querySelector('[data-nkw-done]');
      var customList = box.querySelector('[data-nkw-custom-list]');

      function inputs() { return $all('input[type="checkbox"][data-id]', box); }
      function renderCustom() {
        if (!customList) return;
        customList.innerHTML = saved.custom.map(function (c) {
          var id = 'nkw-c-' + c.id;
          return '<li><label for="' + id + '"><input type="checkbox" id="' + id + '" data-id="' + esc(c.id) + '"' +
            (saved.done[c.id] ? ' checked' : '') + '><span>' + esc(c.text) + '</span></label></li>';
        }).join('');
      }
      function update() {
        var all = inputs(), done = 0;
        all.forEach(function (i) { if (i.checked) done++; });
        var p = percent(done, all.length);
        if (ring) ring.style.setProperty('--p', p);
        if (pctEl) pctEl.textContent = p + '%';
        if (countEl) countEl.textContent = done + ' of ' + all.length + ' done';
      }
      renderCustom();
      inputs().forEach(function (i) { i.checked = !!saved.done[i.getAttribute('data-id')]; });
      box.addEventListener('change', function (e) {
        var i = e.target;
        if (!i.matches || !i.matches('input[type="checkbox"][data-id]')) return;
        var id = i.getAttribute('data-id');
        if (i.checked) saved.done[id] = 1; else delete saved.done[id];
        store.set(key, saved);
        update();
      });
      var add = box.querySelector('[data-nkw-add]');
      if (add) {
        add.addEventListener('submit', function (e) {
          e.preventDefault();
          var field = add.querySelector('input');
          var text = field.value.trim();
          var err = add.parentNode.querySelector('[data-nkw-add-error]');
          if (!text) { if (err) { err.textContent = 'Type an item first, then press Add.'; err.hidden = false; } field.focus(); return; }
          if (err) err.hidden = true;
          saved.custom.push({ id: 'c' + uid(), text: text.slice(0, 140) });
          store.set(key, saved);
          field.value = '';
          renderCustom();
          update();
        });
      }
      var reset = box.querySelector('[data-nkw-reset]');
      if (reset) {
        reset.addEventListener('click', function () {
          confirmInline(reset, 'Clear every tick on this checklist?', function () {
            saved = { done: {}, custom: saved.custom };
            store.set(key, saved);
            inputs().forEach(function (i) { i.checked = false; });
            update();
          });
        });
      }
      update();
    });
  }

  /* ---------- due-date estimator ---------- */
  var DUE_ERRORS = {
    missing: 'Choose the first day of your last period to see an estimate.',
    future: 'That date is in the future. Pick a date on or before today.',
    'too-old': 'That date is more than 44 weeks ago. Check the date and try again.'
  };
  function initDueDate(root) {
    $all('[data-nkw-tool="due-date"]', root).forEach(function (form) {
      if (form.__nkw) return; form.__nkw = true;
      var out = form.querySelector('[data-nkw-out]');
      var err = form.querySelector('[data-nkw-error]');
      var weekBase = form.getAttribute('data-week-url') || '';
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var r = estimateDueDate(form.elements.lmp.value, form.elements.cycle.value);
        if (r.error) {
          err.textContent = DUE_ERRORS[r.error]; err.hidden = false; out.hidden = true;
          form.elements.lmp.setAttribute('aria-invalid', 'true');
          form.elements.lmp.focus();
          return;
        }
        form.elements.lmp.removeAttribute('aria-invalid');
        err.hidden = true;
        var dateText = r.due.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
        var tri = ['first', 'second', 'third'][r.trimester - 1];
        var weekLink = weekBase && r.weeks >= 1 && r.weeks <= 40
          ? ' <a class="nkw-link" href="' + esc(weekBase) + '#week-' + r.weeks + '">Read about week ' + r.weeks + '</a>' : '';
        out.innerHTML = '<span class="nkw-eyebrow">Estimated due date</span><strong>' + esc(dateText) + '</strong>' +
          '<span>About <b>' + r.weeks + ' weeks ' + r.days + ' days</b> along, in the ' + tri + ' trimester.' + weekLink + '</span>';
        out.hidden = false;
      });
    });
  }

  /* ---------- logs, journals and planners ---------- */
  function fieldValue(form, name) { var f = form.elements[name]; return f ? String(f.value || '').trim() : ''; }
  function initLogs(root) {
    $all('[data-nkw-tool="log"]', root).forEach(function (box) {
      if (box.__nkw) return; box.__nkw = true;
      var key = 'log:' + box.getAttribute('data-key');
      var order = box.getAttribute('data-order') || 'newest';
      var form = box.querySelector('form');
      var list = box.querySelector('[data-nkw-list]');
      var summary = box.querySelector('[data-nkw-summary]');
      var err = box.querySelector('[data-nkw-error]');
      var emptyText = box.getAttribute('data-empty') || 'Nothing logged yet.';
      var entries = store.get(key, []);
      if (!Array.isArray(entries)) entries = [];

      // sensible defaults: today's date and the current time
      var now = new Date();
      var pad = function (n) { return (n < 10 ? '0' : '') + n; };
      $all('input[type="date"]', form).forEach(function (i) { if (!i.value && i.hasAttribute('data-today')) i.value = now.getFullYear() + '-' + pad(now.getMonth() + 1) + '-' + pad(now.getDate()); });
      $all('input[type="time"]', form).forEach(function (i) { if (!i.value && i.hasAttribute('data-now')) i.value = pad(now.getHours()) + ':' + pad(now.getMinutes()); });

      function sortKey(e) { return (e.date || '') + 'T' + (e.time || '00:00') + ':' + (e.at || ''); }
      function render() {
        var sorted = entries.slice().sort(function (a, b) {
          var x = sortKey(a), y = sortKey(b);
          return order === 'soonest' ? (x < y ? -1 : x > y ? 1 : 0) : (x < y ? 1 : x > y ? -1 : 0);
        });
        list.innerHTML = sorted.length ? sorted.map(function (e) {
          var when = [e.date ? new Date(e.date + 'T00:00').toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '', e.time || ''].filter(Boolean).join(' · ');
          return '<li><span class="nkw-log__when">' + esc(when || '—') + '</span><span class="nkw-log__what">' + esc(e.kind || e.title || '') +
            (e.amount ? ' · ' + esc(e.amount) : '') + '</span><button type="button" class="nkw-log__del" data-del="' + esc(e.id) + '">Delete<span class="visually-hidden"> entry</span></button>' +
            (e.note ? '<span class="nkw-log__note">' + esc(e.note) + '</span>' : '') + '</li>';
        }).join('') : '<li class="nkw-log__empty"><span class="nkw-log__what">' + esc(emptyText) + '</span></li>';
        if (summary) {
          var today = new Date(); var tKey = today.getFullYear() + '-' + pad(today.getMonth() + 1) + '-' + pad(today.getDate());
          var counts = {};
          entries.forEach(function (e) { if (!e.date || e.date === tKey) counts[e.kind || 'Entries'] = (counts[e.kind || 'Entries'] || 0) + 1; });
          var keys = Object.keys(counts);
          summary.innerHTML = keys.length ? '<span>Today:</span>' + keys.map(function (k) { return '<span>' + esc(k) + ' × ' + counts[k] + '</span>'; }).join('') : '';
        }
      }
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var entry = { id: uid(), at: Date.now() };
        ['date', 'time', 'kind', 'title', 'amount', 'note'].forEach(function (n) { var v = fieldValue(form, n); if (v) entry[n] = v.slice(0, 600); });
        var required = (box.getAttribute('data-required') || '').split(',').filter(Boolean);
        var missing = required.filter(function (n) { return !entry[n]; });
        if (missing.length) {
          err.textContent = 'Fill in ' + missing.map(function (n) { var f = form.elements[n]; var l = f && form.querySelector('label[for="' + f.id + '"]'); return l ? l.textContent.trim().toLowerCase() : n; }).join(' and ') + ' to save this entry.';
          err.hidden = false;
          return;
        }
        err.hidden = true;
        entries.push(entry);
        store.set(key, entries);
        $all('[name="note"],[name="title"],[name="amount"]', form).forEach(function (f) { f.value = ''; });
        render();
        var status = box.querySelector('[data-nkw-saved]');
        if (status) { status.textContent = 'Saved on this device.'; setTimeout(function () { status.textContent = ''; }, 2500); }
      });
      list.addEventListener('click', function (e) {
        var b = e.target.closest('[data-del]');
        if (!b) return;
        entries = entries.filter(function (x) { return x.id !== b.getAttribute('data-del'); });
        store.set(key, entries);
        render();
      });
      var clear = box.querySelector('[data-nkw-clear]');
      if (clear) clear.addEventListener('click', function () {
        confirmInline(clear, 'Delete every entry in this log?', function () { entries = []; store.remove(key); render(); });
      });
      render();
    });
  }

  /* ---------- weekly learning calendar ---------- */
  function initCalendar(root) {
    $all('[data-nkw-calendar]', root).forEach(function (box) {
      if (box.__nkw) return; box.__nkw = true;
      var dataEl = box.querySelector('script[type="application/json"]');
      var ideas = [];
      try { ideas = JSON.parse(dataEl ? dataEl.textContent : '[]'); } catch (e) { ideas = []; }
      var form = box.querySelector('form');
      var out = box.querySelector('[data-nkw-plan]');
      var labels = { colours: 'Colours', stories: 'Storytelling', shapes: 'Shapes', music: 'Music & movement', nature: 'Nature', creativity: 'Creative play', family: 'Family play', numbers: 'Numbers', senses: 'Sensory play', movement: 'Movement', puzzles: 'Puzzles', science: 'Science' };
      var saved = store.get('calendar', null);
      if (saved && form) Object.keys(saved).forEach(function (k) { if (form.elements[k]) form.elements[k].value = saved[k]; });
      function prefs() {
        return { age: form.elements.age.value, minutes: form.elements.minutes.value, place: form.elements.place.value, materials: form.elements.materials.value, interest: form.elements.interest.value };
      }
      function render(shuffle) {
        var p = prefs();
        store.set('calendar', p);
        var plan = buildWeek(ideas, p, JSON.stringify(p) + (shuffle || ''));
        out.innerHTML = plan.map(function (d) {
          if (!d.idea) return '<li class="nkw-day"><small>' + d.day + '</small><b>Free play</b><span>No matching idea. Try other answers above.</span></li>';
          return '<li class="nkw-day"><small>' + d.day + '</small><b>' + esc(d.idea.title) + '</b><span>' + esc(labels[d.theme] || d.theme) + ' · about ' + esc(d.idea.minutes || p.minutes) + ' min · ' + esc(d.idea.place === 'outdoor' ? 'Outdoor' : d.idea.place === 'indoor' ? 'Indoor' : 'Indoor or outdoor') + '</span>' +
            (d.idea.url ? '<a class="nkw-link" href="' + esc(d.idea.url) + '">See activity<span class="visually-hidden">: ' + esc(d.idea.title) + '</span></a>' : '') + '</li>';
        }).join('');
      }
      form.addEventListener('change', function () { render(); });
      form.addEventListener('submit', function (e) { e.preventDefault(); render(String(Date.now())); });
      var shuffleBtn = box.querySelector('[data-nkw-shuffle]');
      if (shuffleBtn) shuffleBtn.addEventListener('click', function () { render(String(Date.now())); });
      render();
    });
  }

  /* ---------- article table of contents ---------- */
  function slug(text) { return String(text).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || 'section'; }
  function initToc(root) {
    $all('[data-nkw-toc]', root).forEach(function (toc) {
      if (toc.__nkw) return; toc.__nkw = true;
      var prose = document.querySelector(toc.getAttribute('data-nkw-toc'));
      if (!prose) return;
      var heads = $all('h2, h3', prose);
      if (heads.length < 2) { toc.hidden = true; return; }
      var seen = {};
      var list = toc.querySelector('ol');
      list.innerHTML = heads.map(function (h) {
        var id = h.id || slug(h.textContent);
        while (seen[id] || (!h.id && document.getElementById(id))) id += '-2';
        seen[id] = true; h.id = id;
        return '<li' + (h.tagName === 'H3' ? ' style="margin-left:.75rem"' : '') + '><a href="#' + id + '">' + esc(h.textContent.trim()) + '</a></li>';
      }).join('');
      toc.hidden = false;
    });
  }

  /* ---------- open a week from a #week-N link ---------- */
  function openFromHash() {
    var h = (location.hash || '').slice(1);
    if (!h) return;
    var el = document.getElementById(h);
    if (!el) return;
    if (el.tagName === 'DETAILS') { el.open = true; el.scrollIntoView({ block: 'start' }); }
    else if (el.tagName === 'DIALOG' && !el.open) {
      if (typeof el.showModal === 'function') el.showModal(); else el.setAttribute('open', '');
    }
  }

  function init(root) {
    initTabs(root); initFilters(root); initDialogs(root); initPrint(root);
    initChecklists(root); initDueDate(root); initLogs(root); initCalendar(root); initToc(root);
  }

  if (typeof document !== 'undefined') {
    var boot = function () { init(document); openFromHash(); };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
    window.addEventListener('hashchange', openFromHash);
    // re-init a section when the theme editor reloads it
    document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  }

  var api = {
    store: store, estimateDueDate: estimateDueDate, percent: percent, matches: matches,
    buildWeek: buildWeek, parseDate: parseDate, tokens: tokens, init: init, slug: slug
  };
  if (typeof window !== 'undefined') window.NuviWorld = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
